import React, { useState, useEffect } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MapPin, Phone, Mail, Clock, ChevronDown, Instagram, Facebook, Sparkles, ExternalLink, Play, Pause, MessageCircle } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Toaster, toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URLs
const LOGOS = {
  white: "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1775335066/saki_white_logo_dchx7o.png",
  mascot: "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1775335067/Naki_Saki_mascot_gqbzpi.png",
};

// Menu items data - expanded for complete grid
const MENU_ITEMS = [
  {
    id: 1,
    name: "Signature Sushi",
    category: "Sushi",
    image: "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199764/16_zhczog.jpg",
    description: "Fresh cuts, artfully crafted"
  },
  {
    id: 2,
    name: "Steamed Bao",
    category: "Bao",
    image: "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199762/11_lcc4h9.jpg",
    description: "Soft, fluffy perfection"
  },
  {
    id: 3,
    name: "Tonkotsu Ramen",
    category: "Ramen",
    image: "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199762/7_dhjm6a.jpg",
    description: "Rich, creamy broth"
  },
  {
    id: 4,
    name: "Gyoza",
    category: "Dumplings",
    image: "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199757/12_yw6ur6.jpg",
    description: "Crispy pan-fried dumplings"
  },
  {
    id: 5,
    name: "Korean Fried Chicken",
    category: "Korean",
    image: "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199757/14_dypubn.jpg",
    description: "Twice-fried, glazed to perfection"
  },
  {
    id: 6,
    name: "Miso Soup",
    category: "Soup",
    image: "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199756/13_y0s7ib.jpg",
    description: "Traditional Japanese comfort"
  },
  {
    id: 7,
    name: "Tempura",
    category: "Fried",
    image: "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199756/1_uaqba0.jpg",
    description: "Light, crispy battered delights"
  },
  {
    id: 8,
    name: "Yakitori",
    category: "Grill",
    image: "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199755/6_ezywv6.jpg",
    description: "Charcoal grilled skewers"
  },
];

// Gallery images - all Naki Saki images combined
const GALLERY_IMAGES = [
  "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199764/16_zhczog.jpg",
  "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199762/11_lcc4h9.jpg",
  "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199762/7_dhjm6a.jpg",
  "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199757/12_yw6ur6.jpg",
  "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199757/14_dypubn.jpg",
  "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199756/13_y0s7ib.jpg",
  "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199756/1_uaqba0.jpg",
  "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199755/6_ezywv6.jpg",
  "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199755/9_hp1zbl.jpg",
  "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199754/8_w4rjll.jpg",
  "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199754/10_pep6th.jpg",
  "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199752/5_habhbp.jpg",
  "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199752/4_x71d7h.jpg",
  "https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199752/17jpg_wcyuaq.jpg",
];

// YouTube Shorts video IDs
const VIDEO_IDS = ["Am9dWfJ4d84", "tlFhZP57D-4", "5npOB49Yh4c"];

// Sakura petal component - REMOVED
// Using Japanese text elements instead

// Floating Japanese Text Elements
const FloatingKanji = ({ char, delay, x, y, size = "text-[100px]" }) => (
  <motion.span
    className={`absolute font-japanese ${size} text-white/[0.03] select-none pointer-events-none`}
    style={{ left: `${x}%`, top: `${y}%` }}
    animate={{ 
      y: [0, -15, 0],
      opacity: [0.02, 0.05, 0.02],
      rotate: [0, 3, 0]
    }}
    transition={{ 
      duration: 6 + delay,
      repeat: Infinity,
      ease: "easeInOut",
      delay: delay * 0.5
    }}
  >
    {char}
  </motion.span>
);

// Navigation component
const Navigation = ({ scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { name: "Home", href: "#home" },
    { name: "About", href: "#about" },
    { name: "Videos", href: "#videos" },
    { name: "Gallery", href: "#gallery" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-black/90 backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="container-custom py-3 md:py-4 flex items-center justify-between">
        <a href="#home" data-testid="nav-logo">
          <img 
            src={LOGOS.white} 
            alt="Naki Saki" 
            className="h-10 sm:h-12 md:h-16 object-contain"
          />
        </a>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-6 lg:gap-8">
          {navItems.map((item) => (
            <a
              key={item.name}
              href={item.href}
              className="nav-link font-body text-xs lg:text-sm uppercase tracking-wider"
              data-testid={`nav-${item.name.toLowerCase()}`}
            >
              {item.name}
            </a>
          ))}
          <a 
            href="https://www.instagram.com/nakisakirestro/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 border border-[#D00000] text-[#D00000] hover:bg-[#D00000] hover:text-white transition-colors text-xs lg:text-sm"
            data-testid="nav-instagram-btn"
          >
            <Instagram size={16} />
            <span className="hidden lg:inline">Follow Us</span>
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-white p-2"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-black/95 backdrop-blur-md"
          >
            <div className="container-custom py-4 flex flex-col gap-3">
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="font-body text-base text-text-secondary hover:text-white transition-colors py-2"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                >
                  {item.name}
                </a>
              ))}
              <a 
                href="https://www.instagram.com/nakisakirestro/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 mt-3 px-4 py-3 bg-[#D00000] text-white"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="mobile-instagram-btn"
              >
                <Instagram size={18} />
                <span>Follow on Instagram</span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

// Japanese Kanji Background Component
const JapaneseTextBg = () => {
  const kanjiChars = ["味", "鮮", "美", "食", "楽", "和", "寿", "司", "麺", "肉", "魚", "茶"];
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.03]">
      {kanjiChars.map((char, i) => (
        <motion.span
          key={i}
          className="absolute font-japanese text-[150px] md:text-[200px] text-white select-none"
          style={{
            left: `${(i % 4) * 25 + 5}%`,
            top: `${Math.floor(i / 4) * 35 + 10}%`,
          }}
          animate={{ 
            opacity: [0.3, 0.6, 0.3],
            scale: [1, 1.05, 1]
          }}
          transition={{ 
            duration: 4 + i * 0.5, 
            repeat: Infinity,
            delay: i * 0.3 
          }}
        >
          {char}
        </motion.span>
      ))}
    </div>
  );
};

// Multiple Dragon Elements Component
const DragonElements = () => (
  <>
    {/* Main Dragon - Right side */}
    <motion.svg 
      viewBox="0 0 400 300" 
      className="absolute right-0 top-1/2 -translate-y-1/2 w-[50%] md:w-[40%] h-auto opacity-[0.06]"
      fill="none" 
      stroke="#D00000" 
      strokeWidth="1"
      animate={{ 
        y: [0, -20, 0],
        opacity: [0.04, 0.08, 0.04]
      }}
      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
    >
      {/* Dragon head */}
      <path d="M320,80 Q340,60 360,70 Q380,80 370,100 Q365,110 350,115 Q340,118 330,110 Q325,105 320,100 Z" strokeWidth="1.5"/>
      {/* Dragon eye */}
      <circle cx="345" cy="85" r="5" fill="#D00000" opacity="0.5"/>
      {/* Dragon body curve */}
      <path d="M320,100 Q280,130 250,120 Q200,100 180,140 Q160,180 120,170 Q80,160 60,190 Q40,220 70,250 Q100,280 140,260 Q180,240 200,270" strokeWidth="2"/>
      {/* Dragon spines */}
      <path d="M300,95 L310,70 M280,115 L290,90 M250,110 L255,85 M220,125 L220,100 M190,145 L185,120 M160,165 L150,145 M130,165 L115,145 M100,175 L80,160" strokeWidth="1"/>
      {/* Dragon whiskers */}
      <path d="M360,75 Q380,65 390,50 M365,80 Q390,75 400,65" strokeWidth="0.8"/>
      {/* Dragon claws */}
      <path d="M200,270 Q210,285 205,300 M200,270 Q215,280 225,295 M200,270 Q220,275 235,285" strokeWidth="1.2"/>
      {/* Tail flames */}
      <path d="M70,250 Q50,240 40,260 Q30,280 50,275 Q70,270 75,255" strokeWidth="1" fill="#D00000" opacity="0.3"/>
    </motion.svg>
    
    {/* Secondary Dragon - Left side smaller */}
    <motion.svg 
      viewBox="0 0 200 150" 
      className="absolute left-5 bottom-20 w-[25%] md:w-[20%] h-auto opacity-[0.04] rotate-[20deg]"
      fill="none" 
      stroke="#D00000" 
      strokeWidth="0.8"
      animate={{ 
        x: [0, 10, 0],
        opacity: [0.03, 0.06, 0.03]
      }}
      transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
    >
      <path d="M150,40 Q170,25 180,35 Q190,45 180,55 L160,60 Q150,55 150,45 Z" />
      <circle cx="165" cy="42" r="3" fill="#D00000" opacity="0.4"/>
      <path d="M150,55 Q120,70 100,60 Q70,45 50,70 Q30,95 50,110 Q80,130 100,115" strokeWidth="1.2"/>
      <path d="M140,50 L145,35 M120,60 L120,45 M90,55 L85,40 M60,75 L50,60" strokeWidth="0.6"/>
    </motion.svg>
  </>
);

// Hero Section
const HeroSection = () => {
  return (
    <section id="home" className="hero-section relative" data-testid="hero-section">
      {/* Background Image - Dark Japanese food */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199752/3_y0xszo.jpg')`
        }}
      />
      <div className="hero-overlay" />
      
      {/* Japanese Kanji Background */}
      <JapaneseTextBg />
      
      {/* Dragon Elements */}
      <DragonElements />
      
      {/* Floating Japanese Text Elements */}
      <FloatingKanji char="龍" delay={0} x={5} y={15} size="text-[80px] md:text-[120px]" />
      <FloatingKanji char="食" delay={1} x={85} y={70} size="text-[60px] md:text-[100px]" />
      <FloatingKanji char="美" delay={2} x={15} y={75} size="text-[70px] md:text-[90px]" />
      <FloatingKanji char="味" delay={3} x={75} y={20} size="text-[50px] md:text-[80px]" />

      {/* Content */}
      <div className="relative z-10 container-custom min-h-screen flex items-center">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center w-full pt-20 md:pt-0">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center md:text-left"
          >
            {/* Highlight Badge */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1 mb-4 border border-[#D00000]/50 bg-[#D00000]/10 rounded-full"
            >
              <Sparkles size={14} className="text-[#D00000]" />
              <span className="font-body text-xs text-[#D00000] uppercase tracking-wider">First Authentic Japanese Fine Dine in Chandannagar</span>
            </motion.div>
            
            <h1 className="font-japanese text-4xl sm:text-5xl md:text-6xl lg:text-8xl text-white mb-4 md:mb-6 leading-tight">
              Naki Saki
            </h1>
            <p className="font-elegant text-xl sm:text-2xl md:text-3xl text-white/80 mb-3 md:mb-4 italic">
              Where Bengal Meets Japan
            </p>
            <p className="font-body text-text-secondary text-sm sm:text-base md:text-lg max-w-lg mx-auto md:mx-0 mb-6 px-4 md:px-0">
              Experience the harmony of Japanese culinary artistry infused with 
              Bengali warmth. Every dish tells a story of tradition and innovation.
            </p>
            
            <div className="flex flex-wrap justify-center md:justify-start gap-3">
              <a href="https://drive.google.com/file/d/1uRV1174TNUvngIkYqkuHkKcDq8XnR4vW/view?usp=sharing" target="_blank" rel="noopener noreferrer">
                <Button 
                  className="btn-primary rounded-none text-sm sm:text-base px-5 sm:px-6 py-4 sm:py-5"
                  data-testid="hero-menu-btn"
                >
                  <ExternalLink size={16} className="mr-2" />
                  View Menu
                </Button>
              </a>
              <a href="https://wa.me/918240523484" target="_blank" rel="noopener noreferrer">
                <Button 
                  variant="outline"
                  className="rounded-none text-sm sm:text-base px-5 sm:px-6 py-4 sm:py-5 border-white/30 text-white hover:bg-white/10"
                  data-testid="hero-reserve-btn"
                >
                  <MessageCircle size={16} className="mr-2" />
                  Book Table
                </Button>
              </a>
            </div>
          </motion.div>

          {/* Mascot with Speech Bubble */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col items-center order-first md:order-last"
          >
            {/* Speech Bubble */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.5 }}
              className="relative mb-4"
            >
              <div className="relative px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl">
                <p className="font-japanese text-lg sm:text-xl md:text-2xl text-white">
                  Hey, Maru Here!
                </p>
                <span className="font-japanese text-xs sm:text-sm text-[#D00000] block">
                  こんにちは！
                </span>
                {/* Speech bubble tail */}
                <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[12px] border-r-[12px] border-t-[12px] border-l-transparent border-r-transparent border-t-white/20" />
              </div>
            </motion.div>
            
            <motion.div
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              className="mascot-container"
            >
              <img 
                src={LOGOS.mascot} 
                alt="Maru - Naki Saki Mascot" 
                className="w-40 sm:w-52 md:w-64 lg:w-80 drop-shadow-2xl"
                data-testid="hero-mascot"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-6 sm:bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="flex flex-col items-center gap-2 text-white/50"
        >
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <ChevronDown size={20} />
        </motion.div>
      </motion.div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="section-spacing relative overflow-hidden" data-testid="about-section">
      {/* Seigaiha pattern background */}
      <div className="absolute inset-0 seigaiha-pattern opacity-30" />
      
      {/* Japanese text watermark */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.span 
          className="absolute -right-20 top-1/4 font-japanese text-[200px] md:text-[300px] text-white/[0.02] select-none"
          animate={{ x: [0, 20, 0] }}
          transition={{ duration: 10, repeat: Infinity }}
        >
          料理
        </motion.span>
      </div>
      
      <div className="container-custom relative z-10">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative order-2 md:order-1"
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src="https://res.cloudinary.com/drcoefnzk/image/upload/q_auto/f_auto/v1776199752/2_edchps.jpg"
                alt="Japanese cuisine at Naki Saki"
                className="w-full h-full object-cover"
              />
              {/* Red accent corner */}
              <div className="absolute top-0 left-0 w-12 md:w-20 h-12 md:h-20 border-l-2 md:border-l-4 border-t-2 md:border-t-4 border-[#D00000]" />
              <div className="absolute bottom-0 right-0 w-12 md:w-20 h-12 md:h-20 border-r-2 md:border-r-4 border-b-2 md:border-b-4 border-[#D00000]" />
            </div>
            
            {/* Floating mascot */}
            <motion.img
              src={LOGOS.mascot}
              alt="Maru"
              className="absolute -bottom-6 md:-bottom-10 -right-4 md:-right-10 w-20 md:w-40 z-10 hidden sm:block"
              animate={{ y: [0, -10, 0], rotate: [0, 5, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="order-1 md:order-2"
          >
            <p className="font-elegant text-[#D00000] text-base md:text-lg tracking-widest mb-3 md:mb-4">OUR STORY</p>
            <h2 className="font-japanese text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-4 md:mb-6 section-title">
              About Naki Saki
            </h2>
            
            <div className="glass-card p-5 md:p-8 mt-6 md:mt-8">
              <p className="font-elegant text-lg md:text-2xl text-white/90 italic mb-4 md:mb-6 leading-relaxed">
                "A culinary bridge between the Land of the Rising Sun and the Bengal Delta"
              </p>
              <p className="font-body text-text-secondary text-sm md:text-base leading-relaxed mb-3 md:mb-4">
                Born in the heart of Chandannagar, Naki Saki brings the authentic flavors of 
                Japan to West Bengal. Our journey began with a simple dream - to create a space 
                where the precision of Japanese cuisine meets the warmth of Bengali hospitality.
              </p>
              <p className="font-body text-text-secondary text-sm md:text-base leading-relaxed">
                Every dish at Naki Saki is crafted with reverence for tradition and a spirit of 
                innovation. From our hand-rolled sushi to our signature ramen, we invite you to 
                experience the artistry of Oriental cuisine.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 md:gap-6 mt-6 md:mt-8">
              {[
                { label: "Premium Ingredients", icon: "🍣" },
                { label: "Authentic Recipes", icon: "📜" },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center glass-card p-4"
                >
                  <span className="text-2xl md:text-3xl mb-2 block">{item.icon}</span>
                  <p className="font-body text-text-secondary text-xs md:text-sm">{item.label}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Menu Section
const MenuSection = () => {
  return (
    <section id="menu" className="section-spacing bg-[#0a0a0a] relative overflow-hidden" data-testid="menu-section">
      {/* Japanese Kanji scattered background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {["寿", "司", "麺", "美", "味", "龍", "火"].map((char, i) => (
          <motion.span 
            key={i} 
            className="absolute font-japanese text-[100px] md:text-[160px] text-white/[0.02] select-none"
            style={{
              left: `${i * 14 + 2}%`,
              top: `${(i % 3) * 30 + 10}%`,
              transform: `rotate(${i * 12 - 30}deg)`
            }}
            animate={{ opacity: [0.015, 0.03, 0.015] }}
            transition={{ duration: 5 + i, repeat: Infinity }}
          >
            {char}
          </motion.span>
        ))}
      </div>
      
      {/* Lantern decorations */}
      <div className="absolute top-10 left-4 md:left-10 w-8 md:w-16 h-12 md:h-24 opacity-20">
        <div className="w-full h-full bg-[#D00000] rounded-full lantern-glow" />
      </div>
      <div className="absolute top-10 right-4 md:right-10 w-8 md:w-16 h-12 md:h-24 opacity-20">
        <div className="w-full h-full bg-[#D00000] rounded-full lantern-glow" style={{ animationDelay: '1.5s' }} />
      </div>

      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10 md:mb-16"
        >
          <p className="font-elegant text-[#D00000] text-base md:text-lg tracking-widest mb-3 md:mb-4">CULINARY EXCELLENCE</p>
          <h2 className="font-japanese text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-3 md:mb-4">
            Our Menu
          </h2>
          <p className="font-body text-text-secondary text-sm md:text-base max-w-2xl mx-auto px-4">
            Discover our carefully curated selection of Japanese and Oriental delicacies, 
            each dish prepared with passion and precision.
          </p>
        </motion.div>

        {/* Complete Grid - 4 columns on desktop, 2 on mobile */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {MENU_ITEMS.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08 }}
              className={`food-card image-zoom cursor-pointer aspect-square ${
                index === 0 ? 'md:col-span-2 md:row-span-2' : ''
              } ${index === 4 ? 'md:col-span-2' : ''}`}
              data-testid={`menu-item-${item.id}`}
            >
              <img
                src={item.image}
                alt={item.name}
                className="w-full h-full object-cover"
              />
              <div className="food-card-content">
                <p className="text-[#D00000] text-xs uppercase tracking-wider mb-1">
                  {item.category}
                </p>
                <h3 className="font-japanese text-base md:text-xl text-white mb-1">{item.name}</h3>
                <p className="font-body text-text-secondary text-xs hidden md:block">{item.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Coming Soon Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex justify-center mt-10"
        >
          <div className="inline-flex items-center gap-3 px-6 py-3 glass-card">
            <Sparkles size={18} className="text-[#D00000]" />
            <span className="font-elegant text-white/80 text-sm md:text-base">More dishes coming soon...</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  return (
    <section id="gallery" className="section-spacing relative overflow-hidden" data-testid="gallery-section">
      {/* Japanese text watermark */}
      <motion.span 
        className="absolute -left-10 top-1/3 font-japanese text-[100px] sm:text-[150px] md:text-[200px] lg:text-[250px] text-white/[0.02] select-none pointer-events-none"
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 8, repeat: Infinity }}
      >
        写真
      </motion.span>
      
      {/* Dragon watermark - hidden on mobile */}
      <motion.svg 
        viewBox="0 0 200 150" 
        className="absolute right-10 bottom-20 w-[15%] md:w-[20%] h-auto opacity-[0.03] -rotate-12 hidden sm:block"
        fill="none" 
        stroke="#D00000" 
        strokeWidth="0.8"
        animate={{ opacity: [0.02, 0.04, 0.02] }}
        transition={{ duration: 6, repeat: Infinity }}
      >
        <path d="M150,40 Q170,25 180,35 Q190,45 180,55 L160,60 Q150,55 150,45 Z" />
        <path d="M150,55 Q120,70 100,60 Q70,45 50,70 Q30,95 50,110 Q80,130 100,115" strokeWidth="1"/>
      </motion.svg>
      
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8 sm:mb-10 md:mb-16"
        >
          <p className="font-elegant text-[#D00000] text-sm sm:text-base md:text-lg tracking-widest mb-2 sm:mb-3 md:mb-4">OUR CULINARY ART</p>
          <h2 className="font-japanese text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-white mb-2 sm:mb-3 md:mb-4">
            Gallery
          </h2>
          <p className="font-body text-text-secondary text-xs sm:text-sm md:text-base max-w-2xl mx-auto px-2">
            Discover our Japanese & Oriental delicacies through our lens
          </p>
        </motion.div>

        {/* Gallery Grid - Masonry style, responsive */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
          {GALLERY_IMAGES.map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className={`image-zoom overflow-hidden rounded-md sm:rounded-lg ${
                index === 0 || index === 5 || index === 10 ? 'sm:col-span-2 sm:row-span-2' : ''
              }`}
              data-testid={`gallery-item-${index}`}
            >
              <img
                src={image}
                alt={`Naki Saki Dish ${index + 1}`}
                className="w-full h-full object-cover min-h-[120px] sm:min-h-[150px] md:min-h-[200px] hover:scale-105 transition-transform duration-500"
              />
            </motion.div>
          ))}
        </div>

        {/* Download Menu CTA - responsive */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex justify-center mt-8 sm:mt-10"
        >
          <a 
            href="https://drive.google.com/file/d/1uRV1174TNUvngIkYqkuHkKcDq8XnR4vW/view?usp=sharing" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-[#D00000] hover:bg-[#6A040F] transition-colors w-full sm:w-auto max-w-xs"
            data-testid="gallery-download-menu-btn"
          >
            <ExternalLink size={18} className="text-white" />
            <span className="font-body text-white text-base sm:text-lg">View Full Menu</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Video Player Component with custom controls (hiding YouTube UI)
const VideoPlayer = ({ videoId }) => {
  const [isMuted, setIsMuted] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [key, setKey] = useState(0);

  const toggleMute = () => {
    setIsMuted(!isMuted);
    setKey(prev => prev + 1);
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
    setKey(prev => prev + 1);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      className="relative aspect-[9/16] bg-black rounded-lg overflow-hidden"
    >
      {/* Top overlay to hide title */}
      <div className="absolute top-0 left-0 right-0 h-28 bg-gradient-to-b from-black from-40% via-black/80 to-transparent z-10 pointer-events-none" />
      
      {/* YouTube iframe */}
      <iframe
        key={key}
        src={`https://www.youtube.com/embed/${videoId}?autoplay=${isPlaying ? 1 : 0}&mute=${isMuted ? 1 : 0}&loop=1&playlist=${videoId}&controls=0&modestbranding=1&rel=0&showinfo=0&iv_load_policy=3&playsinline=1&disablekb=1&fs=0&cc_load_policy=0`}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        title="Naki Saki Video"
        style={{ border: 'none' }}
      />
      
      {/* Bottom overlay to hide YouTube branding */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black via-black/95 to-transparent z-10 pointer-events-none" />
      
      {/* Custom Controls - Always visible at bottom */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
        {/* Play/Pause Button */}
        <button
          onClick={togglePlay}
          className="w-10 h-10 bg-[#D00000] rounded-full flex items-center justify-center hover:bg-[#6A040F] transition-colors shadow-lg"
          title={isPlaying ? "Pause" : "Play"}
          data-testid="video-play-btn"
        >
          {isPlaying ? (
            <Pause size={18} className="text-white" />
          ) : (
            <Play size={18} className="text-white ml-0.5" />
          )}
        </button>
        
        {/* Mute/Unmute Button */}
        <button
          onClick={toggleMute}
          className="w-10 h-10 bg-black/80 rounded-full flex items-center justify-center hover:bg-[#D00000] transition-colors shadow-lg border border-white/20"
          title={isMuted ? "Unmute" : "Mute"}
          data-testid="video-mute-btn"
        >
          {isMuted ? (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
              <line x1="23" y1="9" x2="17" y2="15"/>
              <line x1="17" y1="9" x2="23" y2="15"/>
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07"/>
            </svg>
          )}
        </button>
      </div>
    </motion.div>
  );
};

// Video Section
const VideoSection = () => {
  return (
    <section id="videos" className="section-spacing bg-[#0a0a0a] relative overflow-hidden" data-testid="video-section">
      {/* Japanese text watermark */}
      <motion.span 
        className="absolute right-0 top-1/4 font-japanese text-[100px] sm:text-[150px] md:text-[200px] text-white/[0.02] select-none pointer-events-none"
        animate={{ x: [0, -10, 0] }}
        transition={{ duration: 8, repeat: Infinity }}
      >
        動画
      </motion.span>
      
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8 sm:mb-10 md:mb-16"
        >
          <p className="font-elegant text-[#D00000] text-sm sm:text-base md:text-lg tracking-widest mb-2 sm:mb-3 md:mb-4">EXPERIENCE THE VIBE</p>
          <h2 className="font-japanese text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl text-white mb-2 sm:mb-3 md:mb-4">
            Feel the Vibes
          </h2>
          <p className="font-body text-text-secondary text-xs sm:text-sm md:text-base max-w-2xl mx-auto px-2">
            Get a taste of the Naki Saki experience
          </p>
        </motion.div>

        {/* Video Grid - responsive */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 md:gap-6 max-w-5xl mx-auto">
          {VIDEO_IDS.map((videoId, index) => (
            <VideoPlayer key={index} videoId={videoId} />
          ))}
        </div>

        {/* Download Menu & Social CTA - responsive */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row justify-center items-center gap-3 sm:gap-4 mt-8 sm:mt-10"
        >
          <a 
            href="https://drive.google.com/file/d/1uRV1174TNUvngIkYqkuHkKcDq8XnR4vW/view?usp=sharing" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 sm:gap-3 px-5 sm:px-6 py-3 bg-[#D00000] hover:bg-[#6A040F] transition-colors w-full sm:w-auto"
            data-testid="download-menu-btn"
          >
            <ExternalLink size={16} className="text-white" />
            <span className="font-body text-white text-sm sm:text-base">Download Menu</span>
          </a>
          <a 
            href="https://www.instagram.com/nakisakirestro/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 sm:gap-3 px-5 sm:px-6 py-3 glass-card hover:bg-[#D00000]/10 transition-colors w-full sm:w-auto"
          >
            <Instagram size={18} className="text-[#D00000]" />
            <span className="font-body text-white text-sm sm:text-base">Follow @nakisakirestro</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="section-spacing bg-[#0a0a0a] relative overflow-hidden" data-testid="contact-section">
      {/* Wave decoration */}
      <div className="absolute top-0 left-0 w-full overflow-hidden">
        <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="w-full h-8 md:h-12">
          <path 
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" 
            fill="#050505"
            opacity=".25"
          />
          <path 
            d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" 
            fill="#050505"
            opacity=".5"
          />
          <path 
            d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" 
            fill="#050505"
          />
        </svg>
      </div>
      
      {/* Japanese watermark */}
      <motion.span 
        className="absolute right-0 bottom-1/4 font-japanese text-[120px] md:text-[200px] text-white/[0.02] select-none pointer-events-none"
        animate={{ x: [0, -15, 0] }}
        transition={{ duration: 6, repeat: Infinity }}
      >
        連絡
      </motion.span>

      <div className="container-custom relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10 md:mb-16"
        >
          <p className="font-elegant text-[#D00000] text-base md:text-lg tracking-widest mb-3 md:mb-4">GET IN TOUCH</p>
          <h2 className="font-japanese text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white mb-3 md:mb-4">
            Contact Us
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-20">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="glass-card p-5 md:p-8">
              <h3 className="font-elegant text-xl md:text-2xl text-white mb-5 md:mb-6">Send us a message</h3>
              <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
                <div>
                  <Input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="bg-black/50 border-white/20 text-white placeholder:text-white/40 rounded-none h-11 md:h-12 text-sm md:text-base"
                    data-testid="contact-name-input"
                  />
                </div>
                <div>
                  <Input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="bg-black/50 border-white/20 text-white placeholder:text-white/40 rounded-none h-11 md:h-12 text-sm md:text-base"
                    data-testid="contact-email-input"
                  />
                </div>
                <div>
                  <Input
                    type="tel"
                    name="phone"
                    placeholder="Your Phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-black/50 border-white/20 text-white placeholder:text-white/40 rounded-none h-11 md:h-12 text-sm md:text-base"
                    data-testid="contact-phone-input"
                  />
                </div>
                <div>
                  <Textarea
                    name="message"
                    placeholder="Your Message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="bg-black/50 border-white/20 text-white placeholder:text-white/40 rounded-none resize-none text-sm md:text-base"
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="btn-primary rounded-none w-full py-5 md:py-6 text-sm md:text-base"
                  disabled={isSubmitting}
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </div>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            {/* Google Map Embed - simple query format */}
            <a 
              href="https://maps.app.goo.gl/Fie68oVJvcRKE2F29" 
              target="_blank" 
              rel="noopener noreferrer"
              className="glass-card p-2 overflow-hidden block hover:ring-2 hover:ring-[#D00000]/50 transition-all"
            >
              <iframe
                src="https://maps.google.com/maps?q=Naki+Saki+Chandannagar&t=&z=16&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="200"
                style={{ border: 0, pointerEvents: 'none' }}
                allowFullScreen=""
                loading="lazy"
                className="rounded"
                title="Naki Saki Location"
              />
              <p className="text-center text-[#D00000] text-xs mt-2 font-body">Click to open in Google Maps</p>
            </a>

            <a 
              href="https://maps.app.goo.gl/Fie68oVJvcRKE2F29" 
              target="_blank" 
              rel="noopener noreferrer"
              className="glass-card p-4 md:p-6 block hover:bg-white/5 transition-colors"
            >
              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#D00000]/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-[#D00000]" size={20} />
                </div>
                <div>
                  <h4 className="font-elegant text-base md:text-lg text-white mb-1 md:mb-2">Location</h4>
                  <p className="font-body text-text-secondary text-sm md:text-base">
                    502/A, Grand Trunk Rd, Mankundu<br />
                    Chandannagar, West Bengal 712136
                  </p>
                </div>
              </div>
            </a>

            <a 
              href="tel:+918240523484"
              className="glass-card p-4 md:p-6 block hover:bg-white/5 transition-colors"
            >
              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#D00000]/20 flex items-center justify-center flex-shrink-0">
                  <Phone className="text-[#D00000]" size={20} />
                </div>
                <div>
                  <h4 className="font-elegant text-base md:text-lg text-white mb-1 md:mb-2">Call / WhatsApp</h4>
                  <p className="font-body text-text-secondary text-sm md:text-base">+91 82405 23484</p>
                  <span className="text-[#D00000] text-xs">For Reservation & Booking</span>
                </div>
              </div>
            </a>

            <div className="glass-card p-4 md:p-6">
              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#D00000]/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="text-[#D00000]" size={20} />
                </div>
                <div>
                  <h4 className="font-elegant text-base md:text-lg text-white mb-1 md:mb-2">Hours</h4>
                  <p className="font-body text-text-secondary text-sm md:text-base">
                    Mon - Sun: 12:00 PM - 11:00 PM<br />
                    <span className="text-[#D00000]">Open all days</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex flex-wrap gap-3 md:gap-4 pt-2 md:pt-4">
              <a
                href="https://www.instagram.com/nakisakirestro/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 min-w-[120px] h-12 bg-[#D00000]/20 flex items-center justify-center gap-2 hover:bg-[#D00000] transition-colors"
                data-testid="social-instagram"
              >
                <Instagram size={18} className="text-white" />
                <span className="text-white text-sm">Instagram</span>
              </a>
              <a
                href="https://www.facebook.com/nakisakirestro/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 min-w-[120px] h-12 bg-[#D00000]/20 flex items-center justify-center gap-2 hover:bg-[#D00000] transition-colors"
                data-testid="social-facebook"
              >
                <Facebook size={18} className="text-white" />
                <span className="text-white text-sm">Facebook</span>
              </a>
            </div>
            
            {/* Review CTA */}
            <a 
              href="https://g.page/r/CXTqe0U98VliEBM/review" 
              target="_blank" 
              rel="noopener noreferrer"
              className="block w-full py-3 px-4 bg-[#D00000] text-white text-center text-sm font-medium hover:bg-[#6A040F] transition-colors"
            >
              Leave us a Review on Google
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-6 md:py-8 border-t border-white/10 bg-[#050505]" data-testid="footer">
      <div className="container-custom">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <img 
            src={LOGOS.white} 
            alt="Naki Saki" 
            className="h-8 md:h-10 object-contain"
          />
          <div className="text-center">
            <p className="font-body text-text-secondary text-xs md:text-sm">
              © 2026 Naki Saki. All rights reserved. | Chandannagar, West Bengal
            </p>
            <p className="font-body text-text-secondary/60 text-[10px] md:text-xs mt-1">
              Managed by SAYAN GHOSH
            </p>
          </div>
          <div className="flex gap-4 md:gap-6">
            <a 
              href="https://www.instagram.com/nakisakirestro/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-body text-text-secondary text-xs md:text-sm hover:text-[#D00000] transition-colors flex items-center gap-1"
            >
              <Instagram size={14} />
              <span>Instagram</span>
            </a>
            <a 
              href="https://www.facebook.com/nakisakirestro/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="font-body text-text-secondary text-xs md:text-sm hover:text-[#D00000] transition-colors flex items-center gap-1"
            >
              <Facebook size={14} />
              <span>Facebook</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="App bg-[#050505] min-h-screen grain-overlay">
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#121212',
            color: '#F8F9FA',
            border: '1px solid rgba(255,255,255,0.1)',
          },
        }}
      />
      <Navigation scrolled={scrolled} />
      <HeroSection />
      <AboutSection />
      <VideoSection />
      <GallerySection />
      <ContactSection />
      <Footer />
    </div>
  );
}

export default App;
